<script setup lang="ts">
import UserTabAccount from '@/views/pages/users/UserTabAccount.vue'
import UserBioPanel from '@/views/pages/users/UserBioPanel.vue'
import { App } from '@/types/generated'
import UserInfoData = App.Http.Controllers.Users.UserInfoData
import UserTabStatistics from '@/views/pages/users/UserTabStatistics.vue'

definePage({
  meta: {
    action: 'read',
    subject: 'users',
  },
})

const route = useRoute('apps-user-view-id')
const router = useRouter()
const ability = useAbility()

const userTab = ref(null)
const { t } = useI18n()

const tabs = [
  { icon: 'tabler-user', title: 'account' },
  { icon: 'tabler-chart-bar', title: 'statistics' },
]

const userData = ref<UserInfoData>()
const isSnackbarVisible = ref(false)
const snackbarText = ref('')

const notifyError = (text: string) => {
  snackbarText.value = text
  isSnackbarVisible.value = true
}

const getUserData = async () => {
  const { data } = await useApi<UserInfoData>(`/users/${route.params.id}`)
  if (data?.value)
    userData.value = data.value
}

const switchActivity = async () => {
  try {
    await $api('/users/activity', {
      method: 'PUT',
      body: {
        id: route.params.id,
      },
    })
  }
  catch (error: any) {
    const data = error?.data ?? error?.response?._data

    if (data?.code !== 'seat_limit_reached')
      throw error

    // Активация не прошла: карточка по-прежнему показывает деактивированного пользователя.
    notifyError(data.message)

    return
  }
  await getUserData()
}

await getUserData()

if (userData.value?.roles.includes('admin') && !ability.can('manage', 'all'))
  router.push({ name: 'not-authorized' })
</script>

<template>
  <VRow v-if="userData">
    <VCol
      cols="12"
      md="5"
      lg="4"
    >
      <UserBioPanel
        :user-data="userData"
        @switch-activity="switchActivity"
        @update-user="getUserData"
      />
    </VCol>

    <VCol
      cols="12"
      md="7"
      lg="8"
    >
      <VTabs
        v-model="userTab"
        class="v-tabs-pill"
      >
        <VTab
          v-for="tab in tabs"
          :key="tab.icon"
        >
          <VIcon
            :size="18"
            :icon="tab.icon"
            class="me-1"
          />
          <span>{{ t(`user-detail.${tab.title}.title`) }}</span>
        </VTab>
      </VTabs>

      <VWindow
        v-model="userTab"
        class="mt-6 disable-tab-transition"
        :touch="false"
      >
        <VWindowItem>
          <UserTabAccount :user-data="userData" />
        </VWindowItem>

        <VWindowItem>
          <UserTabStatistics :user-data="userData" />
        </VWindowItem>
      </VWindow>
    </VCol>
  </VRow>
  <div v-else>
    <VAlert
      type="error"
      variant="tonal"
    >
      {{ t(`user-detail.not_found`) }}!
    </VAlert>
  </div>
  <VSnackbar
    v-model="isSnackbarVisible"
    location="top end"
    color="error"
  >
    {{ snackbarText }}
  </VSnackbar>
</template>
